import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, setAccessToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { User } from "../lib/types";
import { Spinner } from "../components/ui";

export function VerifyPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const ran = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const url = new URL(window.location.href);

    // Email link lands here with ?token= — forward to API which does the
    // verification, sets the refresh cookie, and redirects back with ?accessToken=
    const token = url.searchParams.get("token");
    if (token) {
      window.location.href = `${import.meta.env.VITE_API_URL}/auth/verify?token=${encodeURIComponent(token)}`;
      return;
    }

    const accessToken = url.searchParams.get("accessToken");

    if (!accessToken) {
      navigate("/login", { replace: true });
      return;
    }

    url.searchParams.delete("accessToken");
    window.history.replaceState({}, "", url.pathname + url.search);

    (async () => {
      try {
        setAccessToken(accessToken);
        const user = await api.get<User>("/auth/me");
        setSession(accessToken, user);
        navigate(user.name ? "/" : "/onboarding", { replace: true });
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Something went wrong verifying your link. Please try again.",
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="text-center">
        {error ? (
          <>
            <h2 className="text-base font-semibold text-rose-700">Couldn't verify</h2>
            <p className="mt-1 text-sm text-slate-600">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Spinner />
            <p className="mt-3 text-sm text-slate-500">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}
